import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { ContributorSidebar, ContributorTopBar } from '../../../components/prioritization/ContributorSidebar';
import { C } from '../theme/participantTokens';

interface ParticipantShellProps {
  children: ReactNode;
  pageTitle?: string;
  showSidebar?: boolean;
  sessionNav?: ReactNode;
}

export function ParticipantShell({
  children,
  pageTitle = 'Sessions',
  showSidebar = true,
  sessionNav,
}: ParticipantShellProps) {
  const navigate = useNavigate();
  const can = useUserStore((s) => s.can);

  // Use capability check instead of hardcoded role name strings — custom DB-assigned roles
  // (e.g. "Session Facilitator" @Workspace-wide) would never match name-string checks.
  const showAdminView = can('session.facilitate') || can('tracks.configure');

  return (
    <div
      className="pp"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        background: C.bg,
        color: C.textPri,
      }}
    >
      <ContributorTopBar
        pageTitle={pageTitle}
        trailing={(
          <>
            {sessionNav}
            {showAdminView && (
              <button
                type="button"
                onClick={() => navigate('/prioritization/sessions')}
                style={{
                  height: 28,
                  padding: '0 12px',
                  background: 'none',
                  border: `1px solid ${C.ruleBright}`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.6875rem',
                  color: C.textSec,
                }}
              >
                All sessions →
              </button>
            )}
          </>
        )}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0, marginTop: 0 }}>
        {showSidebar && <ContributorSidebar sessionsActive />}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
